import React, { useState, useCallback } from "react";
import {
  TextField,
  Icon,
  BlockStack,
  ResourceList,
  ResourceItem,
  Avatar,
  Text,
} from "@medentic/polaris-react";
import { SearchIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";

export interface Member {
  label: string;
  value: string;
  avatar: string;
}

interface TeammemberSelectProps {
  /** All available options for the user to pick from */
  options: Member[];

  /** Currently selected values (array of strings) */
  selected: string[];

  /** Called when the user toggles an option or removes a tag */
  onChange: (newSelected: string[]) => void;

  /** Localized label for the TextField (defaults to 'Search tags') */
  label?: string;

  /** Localized placeholder text for the TextField (defaults to 'Search tags') */
  placeholder?: string;
  isMultiSelect?: boolean;
}

export const TeammemberSelect: React.FC<TeammemberSelectProps> = ({
  options,
  selected,
  onChange,
  label,
  placeholder,
  isMultiSelect = false,
}) => {
  /**
   * 1) Use the i18n hook
   */
  const { t } = useTranslation();

  /**
   * 2) Provide default localizable text if none is passed via props
   */
  const computedLabel = label ?? t("MultiSelectCombobox.label");
  const computedPlaceholder =
    placeholder ?? t("MultiSelectCombobox.placeholder");

  /**
   * 3) The rest of your existing multi-select logic
   */
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  const escapeRegex = useCallback(
    (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    []
  );

  const updateText = useCallback(
    (value: string) => {
      setInputValue(value);

      if (!value) {
        setFilteredOptions(options);
        return;
      }

      const filterRegex = new RegExp(escapeRegex(value), "i");
      const resultOptions = options.filter((option) =>
        option.label.match(filterRegex)
      );
      setFilteredOptions(resultOptions);
    },
    [options, escapeRegex]
  );

  const handleResourceItemClick = useCallback(
    (value: string) => {
      if (isMultiSelect) {
        const newSelected = selected.includes(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value];
        onChange(newSelected);
      } else {
        onChange([value]);
      }
    },
    [onChange, isMultiSelect, selected]
  );

  const resourceListItems = filteredOptions.slice(0, 10).map((option) => ({
    id: option.value,
    url: "", // Empty string for ResourceItem - we'll handle clicks ourselves
    avatar: option.avatar,
    name: option.label,
    isSelected: selected.includes(option.value),
  }));

  return (
    <BlockStack gap="200">
      <TextField
        prefix={<Icon source={SearchIcon} />}
        onChange={updateText}
        label={computedLabel}
        labelHidden
        value={inputValue}
        placeholder={computedPlaceholder}
        autoComplete="off"
      />

      <ResourceList
        items={resourceListItems}
        renderItem={(item) => {
          const { id, avatar, name, isSelected } = item;
          return (
            <ResourceItem
              id={id}
              onClick={() => handleResourceItemClick(id)}
              media={<Avatar customer name={name} source={avatar} />}
              accessibilityLabel={name}
            >
              <Text as="span" fontWeight={isSelected ? "bold" : "regular"}>
                {name}
              </Text>
            </ResourceItem>
          );
        }}
      />
    </BlockStack>
  );
};

export default TeammemberSelect;
